import { ReactNode, ButtonHTMLAttributes } from 'react';
import { cn } from '../../utils';
import { Prettify } from '@types';

export type ButtonProps = {
    children?: ReactNode;
    icon?: ReactNode;
    iconRight?: boolean;
    loading?: boolean;
    widthFull?: boolean;
    variant?: ButtonVariants;
} & Prettify<ButtonHTMLAttributes<HTMLButtonElement>>;

export type ButtonTypes = 'button' | 'submit' | 'reset';

export type ButtonVariants =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'caution'
    | 'text'
    | 'text-secondary';

function Button({
    children,
    icon = null,
    iconRight = false,
    className = '',
    disabled = false,
    variant = 'primary',
    loading = false,
    type = 'button',
    widthFull = false,
    ...rest
}: ButtonProps) {
    return (
        <button
            {...rest}
            type={type}
            disabled={disabled || loading}
            className={cn(
                'alli-flex alli-items-center alli-self-start alli-text-nowrap alli-text-sm alli-font-medium',
                variants[disabled ? 'disabled' : 'active'][variant],
                {
                    'alli-gap-x-2': !loading && icon,
                    'alli-w-full alli-justify-center': widthFull,
                },
                className,
            )}>
            {!iconRight && loading && <Loading className="alli-mr-2" />}
            {!iconRight && !loading && icon}
            {children}
            {iconRight && !loading && icon}
            {iconRight && loading && <Loading className="alli-ml-2" />}
        </button>
    );
}

Button.displayName = 'Button';
export default Button;

export const variants = {
    disabled: {
        primary:
            'alli-rounded-md alli-px-3 alli-py-2 alli-bg-gray-300 alli-cursor-not-allowed alli-ring-1 alli-ring-inset alli-ring-gray-300 alli-text-gray-600 focus-visible:alli-outline-none',
        secondary:
            'alli-rounded-md alli-px-3 alli-py-2 alli-bg-gray-300 alli-cursor-not-allowed alli-ring-1 alli-ring-inset alli-ring-gray-400 alli-text-gray-600 focus-visible:alli-outline-none',
        tertiary:
            'alli-rounded-md alli-px-3 alli-py-2 alli-bg-gray-300 alli-cursor-not-allowed alli-ring-1 alli-ring-inset alli-ring-gray-400 alli-text-gray-600 focus-visible:alli-outline-none',
        caution:
            'alli-rounded-md alli-px-3 alli-py-2 alli-bg-gray-300 alli-cursor-not-allowed alli-ring-1 alli-ring-inset alli-ring-gray-400 alli-text-gray-600 focus-visible:alli-outline-none',
        text: 'alli-text-gray-600 alli-px-1 alli-py-2  alli-cursor-not-allowed hover:alli-text-gray-600 active:alli-text-gray-600 focus-visible:alli-outline-white',
        'text-secondary':
            'alli-text-gray-600 alli-px-1 alli-py-2 alli-cursor-not-allowed hover:alli-text-gray-600 active:alli-text-gray-600 focus-visible:alli-outline-white',
    },
    active: {
        primary:
            'alli-rounded-md alli-px-3 alli-py-2 alli-bg-blue-600 alli-text-white alli-shadow-sm hover:alli-bg-blue-500 focus-visible:alli-outline focus-visible:alli-outline-2 focus-visible:alli-outline-offset-2 focus-visible:alli-outline-blue-600 active:alli-bg-blue-700',
        secondary:
            'alli-rounded-md alli-px-3 alli-py-2 alli-bg-white alli-text-blue-600 alli-shadow-sm alli-ring-1 alli-ring-inset alli-ring-blue-600 hover:alli-bg-gray-100 focus-visible:alli-outline-blue-600 focus-visible:alli-bg-gray-100 active:alli-bg-blue-gray-50',
        tertiary:
            'alli-rounded-md alli-px-3 alli-py-2 alli-bg-white alli-text-blue-gray-700 alli-shadow-sm alli-ring-1 alli-ring-inset alli-ring-gray-400 active:alli-ring-blue-gray-50 focus-visible:alli-outline-blue-600 hover:alli-bg-gray-100 focus-visible:alli-bg-gray-100 active:alli-bg-blue-gray-50',
        caution:
            'alli-rounded-md alli-px-3 alli-py-2 alli-bg-white alli-text-red-400 alli-shadow-sm alli-ring-1 alli-ring-inset alli-ring-red-400 focus-visible:alli-outline-red-400 active:alli-ring-blue-gray-50 hover:alli-bg-gray-100 focus-visible:alli-ring-red-400 focus-visible:alli-bg-gray-100 active:alli-bg-blue-gray-50',
        text: 'alli-rounded-md alli-px-1 alli-py-2 alli-text-blue-600 hover:alli-text-blue-500 active:alli-text-blue-700 focus-visible:alli-outline-2 focus-visible:alli-outline-offset-2 focus-visible:alli-outline-blue-600',
        'text-secondary':
            'alli-rounded-md alli-px-1 alli-py-2 alli-text-blue-gray-750 hover:alli-text-gray-700 active:alli-text-blue-gray-700 focus-visible:alli-outline-2 focus-visible:alli-outline-offset-2 focus-visible:alli-outline-blue-600',
    },
};

function Loading({ className }: { className: string }) {
    return (
        <svg
            data-testid="Loading"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            className={cn(
                'alli-h-5 alli-w-5 alli-animate-spin alli-fill-current',
                className,
            )}>
            <g strokeWidth="1" fillRule="evenodd">
                <g fillRule="nonzero">
                    <path d="M10,3.5 C6.41015,3.5 3.5,6.41015 3.5,10 C3.5,10.4142 3.16421,10.75 2.75,10.75 C2.33579,10.75 2,10.4142 2,10 C2,5.58172 5.58172,2 10,2 C14.4183,2 18,5.58172 18,10 C18,14.4183 14.4183,18 10,18 C9.58579,18 9.25,17.6642 9.25,17.25 C9.25,16.8358 9.58579,16.5 10,16.5 C13.5899,16.5 16.5,13.5899 16.5,10 C16.5,6.41015 13.5899,3.5 10,3.5 Z"></path>
                </g>
            </g>
        </svg>
    );
}
